import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Copy, ArrowDownCircle, ArrowUpCircle, RefreshCw,
  CheckCircle, Building, Phone, Globe,
  Wallet as WalletIcon, TrendingUp, DollarSign,
  Zap, Shield, Search, Loader, Smartphone,
  FileText, X, ExternalLink, ArrowLeftRight, Eye, EyeOff,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Layout from '../components/Layout';

const P  = '#C29500';
const P2 = '#E8C040';
const G  = '#E8C040';
const R  = '#EF4444';
const C  = '#06B6D4';

const CARD = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12,
};
const INNER = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 10,
};

// Convert currencies + which targets each can convert to (must mirror the
// backend /wallet/convert supported pairs). USD/EUR/GBP only pair with USDT.
const CONV_LABEL = {
  USDT: '🔷 USDT', USD: '💵 USD', NGN: '🇳🇬 NGN', GHS: '🇬🇭 GHS', EUR: '🇪🇺 EUR', GBP: '🇬🇧 GBP',
};
const CONV_TARGETS = {
  USDT: ['USD', 'NGN', 'GHS', 'EUR', 'GBP'],
  USD:  ['USDT'],
  EUR:  ['USDT'],
  GBP:  ['USDT'],
  NGN:  ['USDT', 'GHS'],
  GHS:  ['USDT', 'NGN'],
};

const IS = {
  width: '100%', boxSizing: 'border-box',
  background: '#0D0D12',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10, padding: '10px 14px',
  color: '#fff', fontSize: 13, outline: 'none',
  fontFamily: 'Inter, sans-serif',
};

const Field = ({ label, hint, children }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8' }}>{label}</span>
      {hint && <span style={{ fontSize: 12, color: '#64748B' }}>{hint}</span>}
    </div>
    {children}
  </div>
);

const TxIcon = ({ type }) => {
  if (type === 'deposit')      return <ArrowDownCircle  size={15} style={{ color: G }}/>;
  if (type === 'withdrawal')   return <ArrowUpCircle    size={15} style={{ color: R }}/>;
  if (type === 'subscription') return <TrendingUp       size={15} style={{ color: P2 }}/>;
  if (type === 'referral')     return <DollarSign       size={15} style={{ color: '#FBBF24' }}/>;
  if (type === 'convert')      return <ArrowLeftRight   size={15} style={{ color: C }}/>;
  return <DollarSign size={15} style={{ color: '#64748B' }}/>;
};

const Badge = ({ status }) => {
  const map = {
    completed  : { bg: `${G}14`,      border: `${G}30`,      color: G       },
    pending    : { bg: '#FBBF2414',   border: '#FBBF2430',   color: '#FBBF24' },
    processing : { bg: `${C}14`,      border: `${C}30`,      color: C       },
    failed     : { bg: `${R}14`,      border: `${R}30`,      color: R       },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 6,
      background: s.bg, border: `1px solid ${s.border}`,
      color: s.color, fontWeight: 600, textTransform: 'capitalize',
    }}>
      {status}
    </span>
  );
};

// Flat payout fees — mirrors backend app/services/fees.py
const PAYOUT_FEES = { NGN: 100, GHS: 5 };
const payoutFee = (cur) => PAYOUT_FEES[(cur || '').toUpperCase()] || 0;

const RateCard = ({ rateData, rateLoading, currency, amount }) => {
  const amt     = parseFloat(amount) || 0;
  const rate    = rateData?.our_rate || 0;
  const receive = amt * rate;
  const hasRate = rateData && rateData.currency === currency && rate > 0;
  const isLive  = rateData?.source !== 'Fallback Rate';

  return (
    <div style={{ ...INNER, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: '#0D0D12' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Zap size={11} style={{ color: P2 }}/> Exchange Rate
        </span>
        {hasRate && (
          <span style={{
            fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
            background: isLive ? `${G}14` : '#FBBF2414',
            color: isLive ? G : '#FBBF24',
          }}>
            {isLive ? 'Live' : 'Estimated'}
          </span>
        )}
      </div>
      {rateLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '18px 0' }}>
          <div style={{ width: 14, height: 14, border: `2px solid ${P}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }}/>
          <span style={{ fontSize: 12, color: '#64748B' }}>Fetching rate…</span>
        </div>
      ) : hasRate ? (
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#64748B' }}>1 USDT</span>
            <span style={{ fontWeight: 700, color: '#fff' }}>{rate} {currency}</span>
          </div>
          {amt >= 1 && (() => {
            const fee       = payoutFee(currency);
            const feeUsdt   = rate > 0 ? fee / rate : 0;
            const totalUsdt = amt + feeUsdt;
            return (
            <>
              <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }}/>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: '#64748B' }}>You send</span>
                <span style={{ fontWeight: 600, color: '#fff' }}>${amt.toFixed(2)} USDT</span>
              </div>
              {fee > 0 && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ color: '#64748B' }}>Payout fee</span>
                    <span style={{ fontWeight: 600, color: '#fff' }}>
                      {currency} {fee.toFixed(2)} <span style={{ color: '#475569' }}>(≈ ${feeUsdt.toFixed(2)})</span>
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ color: '#64748B' }}>Total deducted</span>
                    <span style={{ fontWeight: 700, color: '#fff' }}>${totalUsdt.toFixed(2)} USDT</span>
                  </div>
                </>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: `${G}08`, borderRadius: 8, border: `1px solid ${G}18` }}>
                <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>You receive</span>
                <span style={{ fontWeight: 900, color: G }}>{currency} {receive.toFixed(2)}</span>
              </div>
            </>
            );
          })()}
        </div>
      ) : (
        <p style={{ padding: '18px 0', textAlign: 'center', fontSize: 12, color: '#475569' }}>Select a currency to see rate</p>
      )}
    </div>
  );
};

export default function Wallet() {
  const [wallet,          setWallet]          = useState(null);
  const [activeCur,       setActiveCur]       = useState('USDT');
  const [balHidden,       setBalHidden]       = useState(false);
  const [transactions,    setTransactions]    = useState([]);
  const [banks,           setBanks]           = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [accessDenied,    setAccessDenied]    = useState(false);
  const [checking,        setChecking]        = useState(false);
  const [withdrawing,     setWithdrawing]     = useState(false);
  const [tab,             setTab]             = useState('deposit');
  const [copied,          setCopied]          = useState(false);
  const [rateData,        setRateData]        = useState(null);
  const [rateLoading,     setRateLoading]     = useState(false);
  const [verifying,       setVerifying]       = useState(false);
  const [verifiedName,    setVerifiedName]    = useState('');
  const [banksLoading,    setBanksLoading]    = useState(false);
  const [swiftLooking,    setSwiftLooking]    = useState(false);
  const [swiftFound,      setSwiftFound]      = useState(false);
  const [depositTab,      setDepositTab]      = useState('usdt');
  const [bankAccounts,    setBankAccounts]    = useState(null);
  const [copiedField,     setCopiedField]     = useState('');
  const [momoDepositing,  setMomoDepositing]  = useState(false);
  const [momoRateData,    setMomoRateData]    = useState(null);
  const [momoRateLoading, setMomoRateLoading] = useState(false);
  const [momoForm,        setMomoForm]        = useState({
    amount_ghs: '', phone: '', subscriber_no: '', network: 'MTN', customer_name: '', email: '',
  });
  const [receipt,          setReceipt]          = useState(null);
  const [receiptLoading,   setReceiptLoading]   = useState(false);
  const [convertForm,      setConvertForm]      = useState({ from_currency:'USDT', to_currency:'NGN', amount:'' });
  const [converting,       setConverting]       = useState(false);
  const [convertRate,      setConvertRate]      = useState(null);
  const [convertPreview,   setConvertPreview]   = useState(null);
  const [convertPreviewing,setConvertPreviewing]= useState(false);
  const [subWallets,       setSubWallets]       = useState([]);
  const [subWalletsLoading,setSubWalletsLoading]= useState(false);

  const SUPPORTED = [
    { code:'USDT', flag:'🔷', name:'USDT TRC20',    methods:['trc20']                    },
    { code:'NGN',  flag:'🇳🇬', name:'Naira',        methods:['bank']                     },
    { code:'GHS',  flag:'🇬🇭', name:'Cedi',          methods:['bank','momo']              },
    { code:'XOF',  flag:'🌍',  name:'CFA',            methods:['momo']                     },
    { code:'USD',  flag:'🇺🇸', name:'Dollar',        methods:['wire','ach','swift']        },
    { code:'GBP',  flag:'🇬🇧', name:'Pound',         methods:['faster_payments','swift']  },
    { code:'EUR',  flag:'🇪🇺', name:'Euro',           methods:['sepa','swift']             },
  ];

  const INTL = ['USD', 'GBP', 'EUR'];

  const METHOD_LABELS = {
    bank            : { label:'Bank Transfer',   icon:<Building size={13}/>   },
    momo            : { label:'Mobile Money',    icon:<Phone size={13}/>      },
    wire            : { label:'Wire',            icon:<Building size={13}/>   },
    ach             : { label:'ACH',             icon:<DollarSign size={13}/> },
    swift           : { label:'SWIFT',           icon:<Globe size={13}/>      },
    faster_payments : { label:'Faster Payments', icon:<Zap size={13}/>        },
    sepa            : { label:'SEPA',            icon:<Building size={13}/>   },
  };

  const METHOD_HINT = {
    USD: 'Wire/ACH for US banks · SWIFT for international',
    GBP: 'Faster Payments for UK banks · SWIFT for international',
    EUR: 'SEPA for European banks · SWIFT for international',
    GHS: '',
  };

  const [form, setForm] = useState({
    currency:'NGN', method:'bank', source_wallet:'USDT',
    amount_usdt:'', amount_local:'', bank_code:'', bank_name:'',
    account_number:'', account_name:'', phone_number:'',
    routing_number:'', swift_code:'', sort_code:'', iban:'',
    bank_address:'', bank_city:'', bank_country:'',
  });

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (tab !== 'withdraw') return;
    const cur = form.source_wallet !== 'USDT' ? form.source_wallet : form.currency;
    if (cur === 'USDT') return;
    fetchRate(cur);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, form.currency, form.source_wallet]);

  useEffect(() => {
    if (tab !== 'withdraw' || (form.currency === 'USDT' && form.source_wallet === 'USDT')) return;
    if (!INTL.includes(form.currency)) fetchBanks(form.currency, form.method);
    else setBanks([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, form.currency, form.method]);

  useEffect(() => {
    if (form.currency === 'NGN' && form.account_number?.length === 10 && form.bank_code)
      verifyAccount(form.account_number, form.bank_code);
    else setVerifiedName('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.account_number, form.bank_code]);

  useEffect(() => {
    if (tab === 'deposit' && depositTab === 'momo') fetchMomoRate();
    if (tab === 'deposit' && ['usd', 'eur', 'gbp'].includes(depositTab) && !bankAccounts) {
      api.get('/wallet/bank-accounts').then(r => setBankAccounts(r.data)).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, depositTab]);

  const copyField = (key, value) => {
    navigator.clipboard.writeText(value || '');
    setCopiedField(key);
    setTimeout(() => setCopiedField(''), 1500);
  };

  useEffect(() => {
    if (tab !== 'convert') return;
    setConvertPreview(null);
    const { from_currency, to_currency } = convertForm;
    if (from_currency === to_currency) return;
    setConvertPreviewing(true);
    (async () => {
      try {
        const isFiatToFiat = from_currency !== 'USDT' && to_currency !== 'USDT';
        if (isFiatToFiat) {
          const fromUrl = from_currency === 'GHS' ? '/wallet/deposit-rate/ghs' : `/wallet/rate/${from_currency}`;
          const toUrl   = to_currency   === 'GHS' ? '/wallet/deposit-rate/ghs' : `/wallet/rate/${to_currency}`;
          const [fromRes, toRes] = await Promise.all([api.get(fromUrl), api.get(toUrl)]);
          const fromRate  = fromRes.data.our_rate || 1;
          const toRate    = toRes.data.our_rate   || 1;
          const crossRate = toRate / fromRate;
          setConvertPreview({ our_rate: fromRate, to_rate: toRate, cross_rate: crossRate, from: from_currency, to: to_currency, source: fromRes.data.source });
        } else {
          const rateUrl = (from_currency === 'GHS' && to_currency === 'USDT')
            ? '/wallet/deposit-rate/ghs'
            : `/wallet/rate/${from_currency === 'USDT' ? to_currency : from_currency}`;
          const res = await api.get(rateUrl);
          setConvertPreview({ ...res.data, from: from_currency, to: to_currency });
        }
      } catch { setConvertPreview(null); }
      finally { setConvertPreviewing(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, convertForm.from_currency, convertForm.to_currency]);

  const blank = (extra = {}) => ({
    bank_code:'', bank_name:'', account_number:'', account_name:'',
    phone_number:'', routing_number:'', swift_code:'', sort_code:'',
    iban:'', bank_address:'', bank_city:'', bank_country:'',
    amount_usdt:'', amount_local:'', ...extra,
  });

  const setCurrency = (code) => {
    const cur = SUPPORTED.find(c => c.code === code);
    setForm(p => ({ ...p, currency:code, method:cur?.methods[0]||'bank', ...blank() }));
    setVerifiedName(''); setSwiftFound(false); setBanks([]);
  };

  const setMethod = (method) => {
    setForm(p => ({ ...p, method, ...blank() }));
    setVerifiedName(''); setSwiftFound(false); setBanks([]);
  };

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const loadData = async () => {
    setLoading(true);
    try {
      const [w, tx] = await Promise.all([
        api.get('/wallet/info'),
        api.get('/wallet/transactions').catch(() => ({ data:[] })),
      ]);
      setWallet(w.data);
      setTransactions(tx.data || []);
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
      else toast.error('Failed to load wallet!');
    } finally { setLoading(false); }
  };

  const loadSubWallets = async () => {
    setSubWalletsLoading(true);
    try {
      const res = await api.get('/wallet/sub-addresses');
      setSubWallets(res.data?.addresses || []);
    } catch { setSubWallets([]); }
    finally { setSubWalletsLoading(false); }
  };

  useEffect(() => {
    if (tab === 'addresses') loadSubWallets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const fetchRate = async (cur) => {
    if (!cur) return;
    setRateLoading(true); setRateData(null);
    try {
      const res = await api.get(`/wallet/rate/${cur}`);
      setRateData(res.data || null);
    } catch { setRateData(null); }
    finally { setRateLoading(false); }
  };

  const fetchBanks = async (cur, method) => {
    setBanksLoading(true); setBanks([]);
    try {
      const type = method === 'momo' ? 'momo' : 'bank';
      const res  = await api.get(`/wallet/banks?currency=${cur}&type=${type}`);
      setBanks(res.data?.banks || res.data || []);
    } catch { setBanks([]); }
    finally { setBanksLoading(false); }
  };

  const verifyAccount = async (acct, bankCode) => {
    if (!acct || !bankCode || acct.length !== 10) return;
    setVerifying(true); setVerifiedName('');
    try {
      const res = await api.post('/wallet/verify-account', { account_number:acct, bank_code:bankCode });
      if (res.data.success) {
        setVerifiedName(res.data.account_name);
        f('account_name', res.data.account_name);
        toast.success('Account verified!');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Verification failed!'); }
    finally { setVerifying(false); }
  };

  const lookupSwift = async (bic) => {
    if (!bic || bic.length < 8) return;
    setSwiftLooking(true); setSwiftFound(false);
    try {
      const res = await api.get(`/wallet/swift-lookup/${bic.toUpperCase()}`);
      const d   = res.data;
      setForm(p => ({
        ...p,
        bank_name    : d.bank_name    || p.bank_name,
        bank_address : d.bank_address || p.bank_address,
        bank_city    : d.bank_city    || p.bank_city,
        bank_country : d.bank_country || p.bank_country,
      }));
      setSwiftFound(!!d.bank_name);
      if (d.bank_name) toast.success(`Bank: ${d.bank_name}`);
      else toast('Country detected — fill bank name manually.', { icon:'ℹ️' });
    } catch (err) { toast.error(err.response?.data?.detail || 'SWIFT lookup failed!'); }
    finally { setSwiftLooking(false); }
  };

  const fetchMomoRate = async () => {
    setMomoRateLoading(true); setMomoRateData(null);
    try {
      const res = await api.get('/wallet/deposit-rate/ghs');
      setMomoRateData(res.data || null);
    } catch { setMomoRateData(null); }
    finally { setMomoRateLoading(false); }
  };

  const mf = (key, val) => setMomoForm(p => ({ ...p, [key]: val }));

  const handleMomoDeposit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(momoForm.amount_ghs);
    if (!amt || amt < 1) return toast.error('Enter a valid GHS amount!');
    if (!momoForm.phone)         return toast.error('Enter phone number!');
    if (!momoForm.subscriber_no) return toast.error('Enter subscriber number!');
    if (!momoForm.customer_name) return toast.error('Enter your name!');
    setMomoDepositing(true);
    try {
      await api.post('/wallet/deposit/momo', {
        amount_ghs: amt, phone: momoForm.phone, subscriber_no: momoForm.subscriber_no,
        network: momoForm.network, customer_name: momoForm.customer_name,
        email: momoForm.email || undefined,
      });
      toast.success('Payment prompt sent! Approve on your phone to complete.');
      setMomoForm({ amount_ghs:'', phone:'', subscriber_no:'', network:'MTN', customer_name:'', email:'' });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Deposit request failed!');
    } finally { setMomoDepositing(false); }
  };

  const fetchReceipt = async (reference) => {
    setReceiptLoading(true); setReceipt(null);
    try {
      const res = await api.get(`/wallet/deposit/receipt/${reference}`);
      setReceipt(res.data);
    } catch { toast.error('Could not load receipt!'); }
    finally { setReceiptLoading(false); }
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    const amt = parseFloat(convertForm.amount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount!');
    if (convertForm.from_currency === convertForm.to_currency)
      return toast.error('Select different currencies!');
    setConverting(true);
    try {
      const res = await api.post('/wallet/convert', {
        from_currency: convertForm.from_currency,
        to_currency:   convertForm.to_currency,
        amount:        amt,
      });
      const d = res.data;
      toast.success(`Converted ${amt} ${d.from_currency} → ${d.to_amount} ${d.to_currency}!`);
      setConvertRate(d);
      setConvertForm(p => ({ ...p, amount:'' }));
      loadData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Conversion failed!');
    } finally { setConverting(false); }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(wallet?.okx_address || '');
    setCopied(true);
    toast.success('Address copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const checkDeposit = async () => {
    setChecking(true);
    try {
      const res = await api.post('/wallet/check-deposits');
      if (res.data.new_deposits > 0) { toast.success(`${res.data.new_deposits} deposit(s) confirmed!`); loadData(); }
      else toast('No new deposits found.', { icon:'ℹ️' });
    } catch { toast.error('Check failed!'); }
    finally { setChecking(false); }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    const isLocalSrc = form.source_wallet !== 'USDT';
    const amt        = parseFloat(form.amount_usdt);
    if (!isLocalSrc && (!amt || amt < 1)) return toast.error('Enter a valid amount!');

    if (form.currency === 'USDT' && form.source_wallet === 'USDT') {
      if (amt < 5) return toast.error('Minimum withdrawal is 5 USDT!');
      const addr = form.account_number.trim();
      if (!addr.startsWith('T') || addr.length !== 34)
        return toast.error('Enter a valid TRC20 address (starts with T, 34 chars)!');
      setWithdrawing(true);
      try {
        const res = await api.post('/wallet/withdraw/crypto', { amount_usdt:amt, to_address:addr });
        const d = res.data;
        if (d.status === 'completed' && d.txid)
          toast.success(`${d.amount} USDT sent! TX: ${d.txid.slice(0,14)}…`);
        else {
          toast(`${d.amount} USDT queued. Ref: ${d.reference}`, { icon: '⏳' });
          if (d.send_error) console.warn('Send error:', d.send_error);
        }
        setForm(p => ({ ...p, amount_usdt:'', ...blank() }));
        loadData();
      } catch (err) {
        const d = err?.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : d?.message || 'Withdrawal failed!');
      } finally { setWithdrawing(false); }
      return;
    }

    if (INTL.includes(form.currency)) {
      if (amt < 20) return toast.error('Minimum $20 for international withdrawals!');
      if (!form.account_name) return toast.error('Enter account holder name!');
      if (form.currency === 'USD') {
        if (!form.account_number) return toast.error('Enter account number!');
        if (!form.bank_name)      return toast.error('Enter bank name!');
        if (['wire','ach'].includes(form.method) && !form.routing_number) return toast.error('Enter routing number!');
        if (form.method === 'swift' && !form.swift_code) return toast.error('Enter SWIFT/BIC code!');
      }
      if (form.currency === 'GBP') {
        if (!form.bank_name) return toast.error('Enter bank name!');
        if (form.method === 'faster_payments') {
          if (!form.account_number) return toast.error('Enter account number!');
          if (!form.sort_code)      return toast.error('Enter sort code!');
        }
        if (form.method === 'swift' && !form.swift_code) return toast.error('Enter SWIFT/BIC code!');
      }
      if (form.currency === 'EUR') {
        if (!form.iban) return toast.error('Enter IBAN!');
        if (form.method === 'swift' && !form.swift_code) return toast.error('Enter SWIFT/BIC code!');
      }
      setWithdrawing(true);
      try {
        await api.post('/wallet/withdraw/usd', {
          currency:form.currency, amount_usdt:amt, method:form.method,
          account_number:form.account_number||null, iban:form.iban||null,
          account_name:form.account_name, bank_name:form.bank_name||'',
          routing_number:form.routing_number||null, swift_code:form.swift_code||null,
          sort_code:form.sort_code||null, bank_address:form.bank_address||null,
          bank_city:form.bank_city||null, bank_country:form.bank_country||null,
        });
        toast.success(`${form.currency} withdrawal submitted! Processing 1–3 business days.`);
        setForm(p => ({ ...p, amount_usdt:'', ...blank() }));
        setSwiftFound(false); loadData();
      } catch (err) {
        const d = err?.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : 'Withdrawal failed!');
      } finally { setWithdrawing(false); }
      return;
    }

    if (!form.bank_code) return toast.error('Please select a bank!');
    if (form.currency === 'NGN' && form.source_wallet === 'USDT' && !verifiedName)
      return toast.error('Verify your account number first!');
    if (form.method === 'bank' && !form.account_number) return toast.error('Enter account number!');
    if (form.method === 'momo' && !form.phone_number)   return toast.error('Enter phone number!');

    const localAmt = parseFloat(form.amount_local);
    if (isLocalSrc && (!localAmt || localAmt <= 0))
      return toast.error(`Enter a valid ${form.source_wallet} amount!`);

    setWithdrawing(true);
    try {
      const basePayload = isLocalSrc
        ? { source_wallet: form.source_wallet, amount_local: localAmt, amount_usdt: 0 }
        : { source_wallet: 'USDT', amount_usdt: amt, amount_local: 0 };

      const payload = form.method === 'bank'
        ? { ...basePayload, currency:form.currency, bank_code:form.bank_code,
            bank_name:form.bank_name, account_number:form.account_number,
            account_name:form.account_name||'Account Holder' }
        : { ...basePayload, currency:form.currency, bank_code:form.bank_code,
            bank_name:form.bank_name, phone_number:form.phone_number,
            network:form.bank_name, account_name:form.account_name||'Account Holder' };
      const ep = form.method === 'bank' ? '/wallet/withdraw/bank' : '/wallet/withdraw/momo';
      await api.post(ep, payload);
      toast.success('Withdrawal submitted! Processing within 24 hours.');
      setForm(p => ({ ...p, currency:'NGN', method:'bank', source_wallet:'USDT', ...blank() }));
      setVerifiedName(''); setBanks([]);
      loadData();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : Array.isArray(d) ? String(d[0]?.msg||'Validation error') : 'Withdrawal failed!');
    } finally { setWithdrawing(false); }
  };

  const Spin = ({ color = P, size = 14 }) => (
    <div style={{ width: size, height: size, border: `2px solid ${color}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }}/>
  );

  const selBtn = (active) => ({
    padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
    border: `1px solid ${active ? `${P}40` : 'rgba(255,255,255,0.06)'}`,
    background: active ? `${P}14` : 'rgba(255,255,255,0.025)',
    color: active ? P2 : '#64748B',
    fontSize: 12, fontWeight: 700, textAlign: 'center',
  });

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#09090B' }}>
      <div style={{ width: 36, height: 36, border: `3px solid ${P}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }}/>
    </div>
  );

  if (accessDenied) return (
    <Layout>
      <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: `${P}12`, border: `1px solid ${P}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Shield size={28} style={{ color: P2 }}/>
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: '#fff', margin: '0 0 8px' }}>Access Restricted</h2>
          <p style={{ color: '#64748B', fontSize: 13, marginBottom: 20 }}>
            Wallet access isn't enabled for your account. Contact your admin to get access.
          </p>
          <div style={{ padding: '10px 14px', background: `${P}08`, border: `1px solid ${P}15`, borderRadius: 10, fontSize: 12, color: P2 }}>
            Contact admin to enable wallet access.
          </div>
        </div>
      </div>
    </Layout>
  );

  const curMethods        = SUPPORTED.find(c => c.code === form.currency)?.methods || [];
  const showMethodSelector = curMethods.length > 1;
  const isCrypto           = form.currency === 'USDT' && form.source_wallet === 'USDT';
  const isSwiftMethod      = form.method === 'swift';
  const needsRoutingNumber = ['wire','ach'].includes(form.method) && form.currency === 'USD';
  const needsSortCode      = form.method === 'faster_payments' && form.currency === 'GBP';
  const needsIBAN          = form.currency === 'EUR';
  const showOptionalBIC    = form.method === 'sepa' && form.currency === 'EUR';

  // Team roles: only Owner / Finance Manager may move money (withdraw & convert).
  const canMoney = ['owner', 'finance_manager'].includes(wallet?.role || 'owner');
  const TABS = [
    { id:'deposit',   label:'Deposit',   icon:<ArrowDownCircle size={13}/> },
    ...(canMoney ? [
      { id:'withdraw',  label:'Withdraw',  icon:<ArrowUpCircle   size={13}/> },
      { id:'convert',   label:'Convert',   icon:<ArrowLeftRight  size={13}/> },
    ] : []),
    { id:'history',   label:'History',   icon:<FileText        size={13}/> },
    { id:'addresses', label:'Addresses', icon:<WalletIcon      size={13}/> },
  ];

  return (
    <Layout>
      <div style={{ padding: '24px 24px 48px', maxWidth: 740, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: '#fff', margin: 0 }}>Wallet</h1>
            <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>Manage your funds</p>
          </div>
          <motion.button whileHover={{ scale: 1.05 }} onClick={loadData}
            style={{ ...INNER, padding: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RefreshCw size={16} style={{ color: '#64748B' }}/>
          </motion.button>
        </motion.div>

        {/* Interactive multi-currency balance hero */}
        {(() => {
          const currencies = [
            { code: 'USDT', sym: '$', dec: 2, val: wallet?.balance_usdt || 0 },
            { code: 'USD',  sym: '$', dec: 2, val: wallet?.balance_usd  || 0 },
            { code: 'NGN',  sym: '₦', dec: 0, val: wallet?.balance_ngn  || 0 },
            { code: 'GHS',  sym: '₵', dec: 2, val: wallet?.balance_ghs  || 0 },
            { code: 'EUR',  sym: '€', dec: 2, val: wallet?.balance_eur  || 0 },
            { code: 'GBP',  sym: '£', dec: 2, val: wallet?.balance_gbp  || 0 },
          ];
          const active = currencies.find(c => c.code === activeCur) || currencies[0];
          const show = (v, d) => balHidden ? '••••••' : v.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
          return (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              style={{
                position: 'relative', overflow: 'hidden', borderRadius: 18, marginBottom: 20,
                border: '1px solid rgba(194,149,0,0.25)',
                background: 'linear-gradient(135deg, rgba(194,149,0,0.16) 0%, rgba(13,13,18,0.5) 55%, rgba(13,13,18,0.5) 100%)',
              }}>
              <div style={{ position: 'absolute', top: -80, right: -40, width: 260, height: 260, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(232,192,64,0.18), transparent 70%)', pointerEvents: 'none' }}/>
              <div style={{ position: 'relative', padding: '24px 26px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: '#CBD5E1', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Available Balance
                  </span>
                  <button onClick={() => setBalHidden(h => !h)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', display: 'flex', padding: 0 }}>
                    {balHidden ? <EyeOff size={14}/> : <Eye size={14}/>}
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <h2 style={{ fontSize: 38, fontWeight: 900, color: '#fff', margin: 0, letterSpacing: -1.5, lineHeight: 1 }}>
                    <span style={{ fontSize: 22, fontWeight: 700, color: P2 }}>{active.sym}</span>{show(active.val, active.dec)}
                  </h2>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#64748B' }}>{active.code}</span>
                </div>
                {/* Tap a currency to make it active */}
                <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                  {currencies.map(c => {
                    const on = c.code === activeCur;
                    return (
                      <button key={c.code} onClick={() => setActiveCur(c.code)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', transition: 'all .15s',
                          padding: '6px 12px', borderRadius: 20,
                          background: on ? 'rgba(232,192,64,0.18)' : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${on ? 'rgba(232,192,64,0.55)' : 'rgba(255,255,255,0.08)'}`,
                        }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: on ? P2 : '#94A3B8' }}>{c.sym}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#E2E8F0' }}>{show(c.val, c.dec === 0 ? 0 : 2)}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: on ? '#CBD5E1' : '#64748B' }}>{c.code}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          );
        })()}

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, padding: 4, background: '#0D0D12', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto' }}>
          {TABS.map(t => (
            <button key={t.id} type="button" onClick={() => { setTab(t.id); if (t.id === 'addresses') loadSubWallets(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 8, cursor: 'pointer', flex: 1, justifyContent: 'center', whiteSpace: 'nowrap',
                background: tab === t.id ? `linear-gradient(135deg,${P},${P2})` : 'transparent',
                border: 'none', color: tab === t.id ? '#000' : '#64748B',
                fontSize: 12, fontWeight: 700,
              }}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">

          {/* ── Deposit ── */}
          {tab === 'deposit' && (
            <motion.div key="deposit" initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }}
              style={{ ...CARD, overflow: 'hidden' }}>

              <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {[
                  { id:'usdt', label:'USDT (Crypto)',    icon:<ArrowDownCircle size={13}/> },
                  { id:'usd',  label:'USD Bank',          icon:<Building size={13}/> },
                  { id:'eur',  label:'EUR Bank',          icon:<Building size={13}/> },
                  { id:'gbp',  label:'GBP Bank',          icon:<Building size={13}/> },
                  { id:'momo', label:'GHS Mobile Money', icon:<Smartphone size={13}/> },
                ].map(t => (
                  <button key={t.id} type="button" onClick={() => setDepositTab(t.id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      flex: 1, padding: '14px 20px', cursor: 'pointer', background: 'none',
                      border: 'none',
                      borderBottom: depositTab === t.id ? `2px solid ${P}` : '2px solid transparent',
                      color: depositTab === t.id ? P2 : '#64748B',
                      fontSize: 12, fontWeight: 700,
                    }}>
                    {t.icon}{t.label}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">

                {/* USDT deposit */}
                {depositTab === 'usdt' && (
                  <motion.div key="usdt" initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-6 }}
                    style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ ...INNER, padding: 16 }}>
                      <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 8px' }}>Your deposit address (TRC20)</p>
                      <p style={{ fontSize: 12, fontFamily: 'monospace', color: G, wordBreak: 'break-all', lineHeight: 1.6, margin: 0 }}>
                        {wallet?.okx_address || 'Generating…'}
                      </p>
                      <button type="button" onClick={copyAddress}
                        style={{
                          marginTop: 12, width: '100%', padding: '10px 0', borderRadius: 8, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12, fontWeight: 700,
                          background: copied ? `${G}18` : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${copied ? `${G}30` : 'rgba(255,255,255,0.06)'}`,
                          color: copied ? G : '#64748B',
                        }}>
                        {copied ? <><CheckCircle size={13}/> Copied!</> : <><Copy size={13}/> Copy Address</>}
                      </button>
                    </div>

                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {[
                        'Send USDT (TRC20) only — other tokens will be lost',
                        'Minimum deposit: $10 USDT',
                        'Confirms within 5–15 minutes',
                      ].map((tip, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#64748B' }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: `${P}80`, marginTop: 5, flexShrink: 0 }}/>
                          {tip}
                        </li>
                      ))}
                    </ul>

                    <button type="button" onClick={checkDeposit} disabled={checking}
                      style={{
                        width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                        background: `linear-gradient(135deg,${P},${P2})`,
                        color: '#000', fontSize: 13, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        opacity: checking ? 0.7 : 1,
                      }}>
                      {checking ? <><Spin color="#000"/> Checking…</> : <><RefreshCw size={14}/> Check for Deposits</>}
                    </button>
                  </motion.div>
                )}

                {/* USD / EUR / GBP bank-transfer deposit */}
                {['usd', 'eur', 'gbp'].includes(depositTab) && (() => {
                  const cur = depositTab.toUpperCase();
                  const acct = bankAccounts?.accounts?.[cur];
                  const FIELD_LABELS = {
                    account_name: 'Account Name', bank_name: 'Bank Name',
                    account_number: 'Account Number', sort_code: 'Sort Code',
                    swift_code: 'SWIFT / BIC', iban: 'IBAN', bank_address: 'Bank Address',
                  };
                  const fields = Object.keys(FIELD_LABELS)
                    .filter(k => acct?.[k])
                    .map(k => [FIELD_LABELS[k], acct[k], k]);
                  return (
                  <motion.div key={depositTab} initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-6 }}
                    style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>
                      Send a {cur} bank transfer to the account below. <b style={{ color:'#E2E8F0' }}>You must include your reference</b> so we can credit your wallet.
                    </p>

                    {/* Unique reference — highlighted */}
                    <div style={{ ...INNER, padding: 16, border: `1px solid ${P}40`, background: `${P}0D` }}>
                      <p style={{ fontSize: 11, color: '#64748B', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Payment Reference (required)</p>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <span style={{ fontSize: 18, fontWeight: 900, color: P2, fontFamily: 'monospace', letterSpacing: 1 }}>
                          {bankAccounts?.reference || '…'}
                        </span>
                        <button type="button" onClick={() => copyField('reference', bankAccounts?.reference)}
                          style={{ padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                            display: 'flex', alignItems: 'center', gap: 6,
                            background: copiedField === 'reference' ? `${G}18` : 'rgba(255,255,255,0.05)',
                            border: `1px solid ${copiedField === 'reference' ? `${G}30` : 'rgba(255,255,255,0.08)'}`,
                            color: copiedField === 'reference' ? G : '#94A3B8' }}>
                          {copiedField === 'reference' ? <><CheckCircle size={12}/> Copied</> : <><Copy size={12}/> Copy</>}
                        </button>
                      </div>
                    </div>

                    {/* Bank details */}
                    <div style={{ ...INNER, padding: 4 }}>
                      {fields.map(([label, value, key], i, arr) => (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                          padding: '12px 14px', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ fontSize: 10, color: '#64748B', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</p>
                            <p style={{ fontSize: 13, fontWeight: 600, color: '#E2E8F0', margin: 0, wordBreak: 'break-word' }}>{value || '…'}</p>
                          </div>
                          <button type="button" onClick={() => copyField(key, value)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
                              color: copiedField === key ? G : '#475569', display: 'flex' }}>
                            {copiedField === key ? <CheckCircle size={14}/> : <Copy size={14}/>}
                          </button>
                        </div>
                      ))}
                    </div>

                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {[
                        'Always include your reference in the transfer narration/description',
                        'Funds credit after the transfer clears (usually 1–2 business days)',
                        `Send in ${cur} only — currency conversion fees may apply at your bank`,
                      ].map((tip, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#64748B' }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: `${P}80`, marginTop: 5, flexShrink: 0 }}/>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                  );
                })()}

                {/* GHS MoMo deposit */}
                {depositTab === 'momo' && (
                  <motion.div key="momo" initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-6 }}>
                    <form onSubmit={handleMomoDeposit} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

                      <div>
                        <p style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', marginBottom: 8 }}>Network</p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                          {['MTN','VODAFONE','AIRTELTIGO'].map(n => (
                            <button key={n} type="button" onClick={() => mf('network', n)}
                              style={selBtn(momoForm.network === n)}>
                              {n === 'MTN' ? '🟡' : n === 'VODAFONE' ? '🔴' : '🔵'} {n === 'AIRTELTIGO' ? 'AT' : n}
                            </button>
                          ))}
                        </div>
                      </div>

                      <Field label="Amount (GHS)">
                        <input type="number" placeholder="0.00" min="1" step="0.01" required
                          value={momoForm.amount_ghs} onChange={e => mf('amount_ghs', e.target.value)} style={IS}/>
                      </Field>

                      <div style={{ ...INNER, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: '#0D0D12' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Zap size={11} style={{ color: P2 }}/> Exchange Rate
                          </span>
                          {momoRateData && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600, background: `${G}14`, color: G }}>Live</span>}
                        </div>
                        {momoRateLoading ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 0' }}>
                            <Spin/><span style={{ fontSize: 12, color: '#64748B' }}>Fetching rate…</span>
                          </div>
                        ) : momoRateData ? (
                          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                              <span style={{ color: '#64748B' }}>1 USDT</span>
                              <span style={{ fontWeight: 700, color: '#fff' }}>{momoRateData.our_rate} GHS</span>
                            </div>
                            {parseFloat(momoForm.amount_ghs) >= 1 && (
                              <>
                                <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }}/>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                  <span style={{ color: '#64748B' }}>You pay</span>
                                  <span style={{ fontWeight: 600, color: '#fff' }}>GHS {parseFloat(momoForm.amount_ghs).toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: `${G}08`, borderRadius: 8, border: `1px solid ${G}18` }}>
                                  <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>You receive</span>
                                  <span style={{ fontWeight: 900, color: G }}>
                                    ${(parseFloat(momoForm.amount_ghs) / momoRateData.our_rate).toFixed(4)} USDT
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <p style={{ padding: '14px 0', textAlign: 'center', fontSize: 12, color: '#475569' }}>Rate unavailable</p>
                        )}
                      </div>

                      <Field label="Phone Number">
                        <input type="tel" required placeholder="e.g. 0241234567"
                          value={momoForm.phone} onChange={e => mf('phone', e.target.value)} style={IS}/>
                      </Field>

                      <Field label="Subscriber Number" hint="(usually same as phone)">
                        <input type="tel" required placeholder="e.g. 0241234567"
                          value={momoForm.subscriber_no} onChange={e => mf('subscriber_no', e.target.value)} style={IS}/>
                      </Field>

                      <Field label="Account Name">
                        <input type="text" required placeholder="Name on MoMo account"
                          value={momoForm.customer_name} onChange={e => mf('customer_name', e.target.value)} style={IS}/>
                      </Field>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', ...INNER, fontSize: 12, color: '#64748B' }}>
                        <Smartphone size={12} style={{ color: P2, flexShrink: 0, marginTop: 1 }}/>
                        You'll receive a payment prompt on your phone. Approve it to complete the deposit. Funds reflect instantly after approval.
                      </div>

                      <button type="submit" disabled={momoDepositing}
                        style={{
                          width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                          background: `linear-gradient(135deg,${P},${P2})`,
                          color: '#000', fontSize: 13, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          opacity: momoDepositing ? 0.7 : 1,
                        }}>
                        {momoDepositing
                          ? <><Spin color="#000"/> Sending prompt…</>
                          : <><Smartphone size={15}/> Deposit via {momoForm.network}</>}
                      </button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* ── Withdraw ── */}
          {tab === 'withdraw' && (
            <motion.div key="withdraw" initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }}
              style={{ ...CARD, overflow: 'hidden' }}>

              <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#fff', margin: 0 }}>
                    <ArrowUpCircle size={15} style={{ color: P2 }}/> Withdraw Funds
                  </h3>
                  <p style={{ fontSize: 12, color: '#64748B', margin: '2px 0 0' }}>Min $10 · Processed within 24h</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 2px' }}>Available</p>
                  <p style={{ fontSize: 14, fontWeight: 900, color: G, margin: 0 }}>
                    {form.source_wallet === 'GHS'
                      ? `GHS ${(wallet?.balance_ghs||0).toFixed(2)}`
                      : form.source_wallet === 'NGN'
                      ? `₦${(wallet?.balance_ngn||0).toFixed(0)}`
                      : `$${(wallet?.balance_usdt||0).toFixed(2)} USDT`}
                  </p>
                </div>
              </div>

              <form onSubmit={handleWithdraw} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* Source wallet */}
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', marginBottom: 8 }}>Pay from</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    {[
                      { id:'USDT', label:'🔷 USDT', bal:`$${(wallet?.balance_usdt||0).toFixed(2)}` },
                      { id:'GHS',  label:'🇬🇭 GHS',  bal:`GHS ${(wallet?.balance_ghs||0).toFixed(2)}` },
                      { id:'NGN',  label:'🇳🇬 NGN',  bal:`₦${(wallet?.balance_ngn||0).toFixed(0)}` },
                    ].map(w => (
                      <button key={w.id} type="button"
                        onClick={() => {
                          const cur = w.id === 'GHS' ? 'GHS' : w.id === 'NGN' ? 'NGN' : form.currency;
                          setRateData(null);
                          setForm(p => ({ ...p, source_wallet:w.id, currency:cur, ...blank() }));
                          setVerifiedName(''); setBanks([]);
                        }}
                        style={selBtn(form.source_wallet === w.id)}>
                        <div>{w.label}</div>
                        <div style={{ color: '#475569', fontSize: 10, marginTop: 2 }}>{w.bal}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Currency selector */}
                {form.source_wallet === 'USDT' ? (
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', marginBottom: 8 }}>Currency</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
                      {SUPPORTED.map(cur => (
                        <button key={cur.code} type="button" onClick={() => setCurrency(cur.code)}
                          style={{ ...selBtn(form.currency === cur.code), display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span>{cur.flag}</span><span>{cur.code}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ ...INNER, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: '#64748B' }}>Withdraw currency</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>
                      {form.source_wallet === 'GHS' ? '🇬🇭 GHS' : '🇳🇬 NGN'}
                    </span>
                  </div>
                )}

                {/* Method selector */}
                {showMethodSelector && (
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', marginBottom: 8 }}>Method</p>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${curMethods.length},1fr)`, gap: 8 }}>
                      {curMethods.map(m => {
                        const ml = METHOD_LABELS[m];
                        return (
                          <button key={m} type="button" onClick={() => setMethod(m)}
                            style={{ ...selBtn(form.method === m), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            {ml?.icon} {ml?.label}
                          </button>
                        );
                      })}
                    </div>
                    {METHOD_HINT[form.currency] && (
                      <p style={{ fontSize: 11, color: '#475569', marginTop: 6 }}>{METHOD_HINT[form.currency]}</p>
                    )}
                  </div>
                )}

                {/* Amount */}
                {form.source_wallet !== 'USDT' ? (
                  <Field label={`Amount (${form.source_wallet})`}>
                    <input type="number" placeholder="0.00" min="1" step="0.01" required
                      value={form.amount_local} onChange={e => f('amount_local', e.target.value)} style={IS}/>
                  </Field>
                ) : (
                  <Field label="Amount (USDT)">
                    <input type="number" placeholder="0.00" min="1" step="0.01" required
                      value={form.amount_usdt} onChange={e => f('amount_usdt', e.target.value)} style={IS}/>
                  </Field>
                )}

                {/* Rate card */}
                {!isCrypto && form.source_wallet === 'USDT' && (
                  <RateCard rateData={rateData} rateLoading={rateLoading}
                    currency={form.currency} amount={form.amount_usdt}/>
                )}

                {/* Local-currency payout fee breakdown */}
                {form.source_wallet !== 'USDT' && payoutFee(form.source_wallet) > 0 && parseFloat(form.amount_local) > 0 && (
                  <div style={{ ...INNER, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: '#0D0D12' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Zap size={11} style={{ color: P2 }}/> Breakdown
                      </span>
                    </div>
                    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#64748B' }}>You withdraw</span>
                        <span style={{ fontWeight: 600, color: '#fff' }}>
                          {form.source_wallet} {(parseFloat(form.amount_local) || 0).toFixed(2)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#64748B' }}>Payout fee</span>
                        <span style={{ fontWeight: 600, color: '#fff' }}>
                          {form.source_wallet} {payoutFee(form.source_wallet).toFixed(2)}
                        </span>
                      </div>
                      <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }}/>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: `${G}08`, borderRadius: 8, border: `1px solid ${G}18` }}>
                        <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>Total deducted</span>
                        <span style={{ fontWeight: 900, color: G }}>
                          {form.source_wallet} {((parseFloat(form.amount_local) || 0) + payoutFee(form.source_wallet)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* USDT TRC20 */}
                {isCrypto && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <Field label="Destination Address (TRC20)">
                      <input type="text" required placeholder="TRC20 wallet address (starts with T)"
                        value={form.account_number}
                        onChange={e => f('account_number', e.target.value.trim())}
                        style={{ ...IS, fontFamily: 'monospace', fontSize: 12 }}/>
                    </Field>
                    {parseFloat(form.amount_usdt) >= 5 && (
                      <div style={{ ...INNER, overflow: 'hidden' }}>
                        <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: '#0D0D12' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Zap size={11} style={{ color: P2 }}/> Breakdown
                          </span>
                        </div>
                        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {[
                            ['You send',   `${parseFloat(form.amount_usdt).toFixed(2)} USDT`],
                            ['Network fee','1.00 USDT'],
                          ].map(([l,v]) => (
                            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                              <span style={{ color: '#64748B' }}>{l}</span>
                              <span style={{ fontWeight: 600, color: '#fff' }}>{v}</span>
                            </div>
                          ))}
                          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }}/>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: `${G}08`, borderRadius: 8, border: `1px solid ${G}18` }}>
                            <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>Total deducted</span>
                            <span style={{ fontWeight: 900, color: G }}>{(parseFloat(form.amount_usdt) + 1).toFixed(2)} USDT</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', ...INNER, fontSize: 12, color: '#64748B' }}>
                      <Shield size={11} style={{ color: '#FBBF24', flexShrink: 0, marginTop: 1 }}/>
                      Sends on TRC20 network only. Double-check the address — crypto transactions are irreversible. Min 5 USDT.
                    </div>
                  </div>
                )}

                {/* African: bank/momo */}
                {!INTL.includes(form.currency) && !isCrypto && (
                  <Field label={form.method === 'momo' ? 'Network' : 'Bank'}>
                    {banksLoading ? (
                      <div style={{ ...INNER, padding: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Spin/><span style={{ fontSize: 12, color: '#64748B' }}>Loading…</span>
                      </div>
                    ) : (
                      <select required value={form.bank_code}
                        onChange={e => {
                          const b = banks.find(b => b.code === e.target.value);
                          setForm(p => ({ ...p, bank_code:e.target.value, bank_name:b?.name||'' }));
                        }}
                        style={{ ...IS }}>
                        <option value="">{form.method === 'momo' ? 'Select network' : 'Select bank'}</option>
                        {banks.map((b,i) => <option key={i} value={b.code}>{b.name}</option>)}
                      </select>
                    )}
                  </Field>
                )}

                {/* Account number */}
                {!INTL.includes(form.currency) && !isCrypto && form.method === 'bank' && (
                  <Field label="Account Number" hint={form.currency === 'NGN' ? `${form.account_number.length}/10` : undefined}>
                    <div style={{ position: 'relative' }}>
                      <input type="text" required
                        placeholder={form.currency === 'NGN' ? '10-digit account number' : 'Account number'}
                        value={form.account_number}
                        maxLength={form.currency === 'NGN' ? 10 : 20}
                        onChange={e => f('account_number',
                          form.currency === 'NGN' ? e.target.value.replace(/\D/g,'').slice(0,10) : e.target.value
                        )}
                        style={{ ...IS, borderColor: verifiedName ? `${G}40` : 'rgba(255,255,255,0.08)', paddingRight: 36 }}/>
                      <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                        {verifying    && <Spin/>}
                        {verifiedName && !verifying && <CheckCircle size={15} style={{ color: G }}/>}
                      </div>
                    </div>
                  </Field>
                )}

                {/* Account name */}
                {!INTL.includes(form.currency) && !isCrypto && form.method === 'bank' && (
                  <Field label="Account Name" hint={verifiedName ? <span style={{ color: G, display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={10}/> Verified</span> : undefined}>
                    {form.currency === 'NGN' ? (
                      <div style={{ ...IS, minHeight: 42, display: 'flex', alignItems: 'center', borderColor: verifiedName ? `${G}40` : 'rgba(255,255,255,0.08)' }}>
                        {verifying ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748B', fontSize: 12 }}>
                            <Spin/> Verifying…
                          </span>
                        ) : verifiedName ? (
                          <span style={{ color: G, fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <CheckCircle size={13}/> {verifiedName}
                          </span>
                        ) : (
                          <span style={{ color: '#475569', fontSize: 12 }}>Enter account number and select bank to auto-verify</span>
                        )}
                      </div>
                    ) : (
                      <input type="text" required placeholder="Account holder name"
                        value={form.account_name} onChange={e => f('account_name', e.target.value)} style={IS}/>
                    )}
                  </Field>
                )}

                {/* MoMo phone */}
                {!isCrypto && form.method === 'momo' && (
                  <Field label="Phone Number">
                    <input type="tel" required placeholder={form.currency === 'GHS' ? '0XX XXX XXXX' : 'Phone number'}
                      value={form.phone_number} onChange={e => f('phone_number', e.target.value)} style={IS}/>
                  </Field>
                )}

                {/* International fields */}
                {!isCrypto && INTL.includes(form.currency) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {isSwiftMethod && (
                      <Field label="SWIFT / BIC Code">
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input type="text" required placeholder="e.g. BARCGB22XXX"
                            value={form.swift_code} maxLength={11}
                            onChange={e => { f('swift_code', e.target.value.toUpperCase()); setSwiftFound(false); }}
                            style={{ ...IS, flex: 1, fontFamily: 'monospace', textTransform: 'uppercase', borderColor: swiftFound ? `${G}40` : 'rgba(255,255,255,0.08)' }}/>
                          <button type="button" onClick={() => lookupSwift(form.swift_code)}
                            disabled={swiftLooking || form.swift_code.length < 8}
                            style={{
                              padding: '0 14px', borderRadius: 10, cursor: 'pointer', whiteSpace: 'nowrap',
                              background: `${C}12`, border: `1px solid ${C}30`,
                              color: C, fontSize: 12, fontWeight: 700,
                              display: 'flex', alignItems: 'center', gap: 6,
                              opacity: swiftLooking || form.swift_code.length < 8 ? 0.4 : 1,
                            }}>
                            {swiftLooking ? <Loader size={13} style={{ animation: 'spin .7s linear infinite' }}/> : <Search size={13}/>}
                            Lookup
                          </button>
                        </div>
                      </Field>
                    )}
                    {showOptionalBIC && (
                      <Field label="BIC / SWIFT" hint="(optional)">
                        <input type="text" placeholder="e.g. DEUTDEDB" value={form.swift_code} maxLength={11}
                          onChange={e => f('swift_code', e.target.value.toUpperCase())}
                          style={{ ...IS, fontFamily: 'monospace', textTransform: 'uppercase' }}/>
                      </Field>
                    )}
                    {needsRoutingNumber && (
                      <Field label="Routing Number (ABA)" hint={`${form.routing_number.length}/9`}>
                        <input type="text" required placeholder="9-digit routing number"
                          value={form.routing_number} maxLength={9}
                          onChange={e => f('routing_number', e.target.value.replace(/\D/g,'').slice(0,9))} style={IS}/>
                      </Field>
                    )}
                    {needsSortCode && (
                      <Field label="Sort Code">
                        <input type="text" required placeholder="e.g. 20-00-00"
                          value={form.sort_code} maxLength={8}
                          onChange={e => f('sort_code', e.target.value)} style={IS}/>
                      </Field>
                    )}
                    {(needsIBAN || (form.currency === 'GBP' && isSwiftMethod)) ? (
                      <Field label={needsIBAN ? 'IBAN' : 'IBAN / Account Number'}>
                        <input type="text" required={needsIBAN}
                          placeholder={needsIBAN ? 'e.g. DE89370400440532013000' : 'IBAN or account number'}
                          value={needsIBAN ? form.iban : form.account_number}
                          onChange={e => f(needsIBAN ? 'iban' : 'account_number', e.target.value.toUpperCase().replace(/\s/g,''))}
                          style={{ ...IS, fontFamily: 'monospace', textTransform: 'uppercase' }}/>
                      </Field>
                    ) : (
                      <Field label="Account Number">
                        <input type="text" required placeholder="Account number"
                          value={form.account_number} onChange={e => f('account_number', e.target.value)} style={IS}/>
                      </Field>
                    )}
                    <Field label="Bank Name" hint={form.currency === 'EUR' ? '(optional)' : undefined}>
                      <input type="text" required={form.currency !== 'EUR'} placeholder="Bank name"
                        value={form.bank_name} onChange={e => f('bank_name', e.target.value)}
                        style={{ ...IS, borderColor: swiftFound ? `${G}40` : 'rgba(255,255,255,0.08)' }}/>
                    </Field>
                    <Field label="Account Holder Name">
                      <input type="text" required placeholder="Full name on account"
                        value={form.account_name} onChange={e => f('account_name', e.target.value)} style={IS}/>
                    </Field>
                    {isSwiftMethod && (form.bank_address || form.bank_city || form.bank_country) && (
                      <div style={{ padding: 12, background: `${C}06`, border: `1px solid ${C}18`, borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <p style={{ fontSize: 11, color: C, fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Globe size={11}/> Bank Details
                          <span style={{ color: G, fontWeight: 400, display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={10}/> Auto-filled</span>
                        </p>
                        {[['Address','bank_address'],['City','bank_city'],['Country','bank_country']]
                          .filter(([,k]) => form[k]).map(([label, key]) => (
                          <div key={key}>
                            <p style={{ fontSize: 11, color: '#64748B', margin: '0 0 2px' }}>{label}</p>
                            <input type="text" value={form[key]} onChange={e => f(key, e.target.value)}
                              style={{ width: '100%', background: 'transparent', color: '#fff', fontSize: 12, padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.08)', outline: 'none' }}/>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', ...INNER, fontSize: 12, color: '#64748B' }}>
                      <Shield size={11} style={{ color: '#FBBF24', flexShrink: 0 }}/>
                      0.5% processing fee · Min $20 ·{' '}
                      {form.currency === 'EUR' && form.method === 'sepa' ? '1–2 business days' : '1–3 business days'}
                    </div>
                  </div>
                )}

                <button type="submit"
                  disabled={withdrawing || (!isCrypto && form.currency === 'NGN' && form.method === 'bank' && !verifiedName)}
                  style={{
                    width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: (withdrawing || (!isCrypto && form.currency === 'NGN' && form.method === 'bank' && !verifiedName))
                      ? 'rgba(255,255,255,0.04)'
                      : `linear-gradient(135deg,${P},${P2})`,
                    color: (withdrawing || (!isCrypto && form.currency === 'NGN' && form.method === 'bank' && !verifiedName))
                      ? '#475569' : '#000',
                    fontSize: 13, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                  {withdrawing ? <Spin color="#000"/> :
                   isCrypto ? <><ArrowUpCircle size={15}/> Send USDT via TRC20</> :
                   form.currency === 'GBP' ? <><Globe size={15}/> Withdraw GBP via {form.method === 'faster_payments' ? 'Faster Payments' : 'SWIFT'}</> :
                   INTL.includes(form.currency) ? <><Globe size={15}/> Withdraw {form.currency} via {form.method.toUpperCase()}</> :
                   form.method === 'momo' ? <><Phone size={15}/> Withdraw via MoMo</> :
                   <><ArrowUpCircle size={15}/> Withdraw to Bank</>}
                </button>
              </form>
            </motion.div>
          )}

          {/* ── Convert ── */}
          {tab === 'convert' && (
            <motion.div key="convert" initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }}
              style={{ ...CARD, overflow: 'hidden' }}>

              <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <ArrowLeftRight size={15} style={{ color: P2 }}/>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Convert Between Wallets</span>
              </div>

              <form onSubmit={handleConvert} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  {[
                    { code:'USDT', label:`$${(wallet?.balance_usdt||0).toFixed(2)}`,    color: G  },
                    { code:'GHS',  label:`GHS ${(wallet?.balance_ghs||0).toFixed(2)}`,  color: P2 },
                    { code:'NGN',  label:`₦${(wallet?.balance_ngn||0).toFixed(0)}`,     color: C  },
                  ].map(b => (
                    <div key={b.code} style={{ ...INNER, padding: '10px 8px', textAlign: 'center' }}>
                      <p style={{ fontSize: 11, color: '#64748B', margin: '0 0 2px' }}>{b.code}</p>
                      <p style={{ fontSize: 12, fontWeight: 900, color: b.color, margin: 0 }}>{b.label}</p>
                    </div>
                  ))}
                </div>

                <Field label="From">
                  <select value={convertForm.from_currency}
                    onChange={e => {
                      const frm = e.target.value;
                      const targets = CONV_TARGETS[frm] || ['USDT'];
                      setConvertForm(p => ({
                        ...p, from_currency: frm,
                        to_currency: targets.includes(p.to_currency) ? p.to_currency : targets[0],
                      }));
                    }}
                    style={IS}>
                    {Object.keys(CONV_TARGETS).map(c => (
                      <option key={c} value={c}>{CONV_LABEL[c]}</option>
                    ))}
                  </select>
                </Field>

                <Field label="To">
                  <select value={convertForm.to_currency}
                    onChange={e => setConvertForm(p => ({ ...p, to_currency: e.target.value }))}
                    style={IS}>
                    {(CONV_TARGETS[convertForm.from_currency] || ['USDT']).map(c => (
                      <option key={c} value={c}>{CONV_LABEL[c]}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Amount" hint={`(${convertForm.from_currency})`}>
                  <input type="number" step="any" min="0" placeholder="0.00"
                    value={convertForm.amount}
                    onChange={e => setConvertForm(p => ({ ...p, amount: e.target.value }))}
                    style={IS}/>
                </Field>

                {/* Rate preview */}
                <div style={{ ...INNER, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: '#0D0D12' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Zap size={11} style={{ color: P2 }}/> Rate Breakdown
                    </span>
                    {convertPreview && (
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                        background: convertPreview.source !== 'Fallback Rate' ? `${G}14` : '#FBBF2414',
                        color: convertPreview.source !== 'Fallback Rate' ? G : '#FBBF24',
                      }}>
                        {convertPreview.source !== 'Fallback Rate' ? 'Live' : 'Estimated'}
                      </span>
                    )}
                  </div>
                  {convertPreviewing ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 0' }}>
                      <Spin/><span style={{ fontSize: 12, color: '#64748B' }}>Fetching rate…</span>
                    </div>
                  ) : convertPreview ? (() => {
                    const amt = parseFloat(convertForm.amount) || 0;
                    const frm = convertForm.from_currency;
                    const to  = convertForm.to_currency;
                    const rate = convertPreview.our_rate || 0;
                    let rateLabel, estOut;
                    if (frm === 'USDT') {
                      rateLabel = `1 USDT = ${rate} ${to}`;
                      estOut    = amt > 0 ? `${(amt * rate).toFixed(2)} ${to}` : null;
                    } else if (to === 'USDT') {
                      rateLabel = `${rate} ${frm} = 1 USDT`;
                      estOut    = amt > 0 ? `$${(amt / rate).toFixed(4)} USDT` : null;
                    } else {
                      const cross = convertPreview.cross_rate || 0;
                      rateLabel = cross > 0 ? `1 ${frm} = ${(cross * 0.995).toFixed(4)} ${to}` : 'Via USDT mid-rate';
                      estOut    = amt > 0 && cross > 0 ? `${(amt * cross * 0.995).toFixed(2)} ${to}` : null;
                    }
                    const isFiatFiat = frm !== 'USDT' && to !== 'USDT';
                    return (
                      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#64748B' }}>Rate</span>
                          <span style={{ color: '#fff', fontWeight: 700 }}>{rateLabel}</span>
                        </div>
                        {isFiatFiat && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748B' }}>Conversion fee</span>
                            <span style={{ color: '#FBBF24', fontWeight: 700 }}>0.5%</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#64748B' }}>You send</span>
                          <span style={{ color: '#fff', fontWeight: 700 }}>{amt > 0 ? `${amt} ${frm}` : '—'}</span>
                        </div>
                        {estOut && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 8 }}>
                            <span style={{ color: '#64748B' }}>You receive ~</span>
                            <span style={{ color: G, fontWeight: 900 }}>{estOut}</span>
                          </div>
                        )}
                        {convertRate && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 8 }}>
                            <span style={{ color: '#475569' }}>Last converted</span>
                            <span style={{ color: C, fontWeight: 700 }}>{convertRate.from_amount} {convertRate.from_currency} → {convertRate.to_amount} {convertRate.to_currency}</span>
                          </div>
                        )}
                      </div>
                    );
                  })() : (
                    <p style={{ padding: '14px 0', textAlign: 'center', fontSize: 12, color: '#475569' }}>Select currencies to see rate</p>
                  )}
                </div>

                <button type="submit" disabled={converting || !convertForm.amount}
                  style={{
                    width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: `linear-gradient(135deg,${P},${P2})`,
                    color: '#000', fontSize: 13, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    opacity: converting || !convertForm.amount ? 0.5 : 1,
                  }}>
                  {converting ? <><Spin color="#000"/> Converting…</> : <><ArrowLeftRight size={15}/> Convert Now</>}
                </button>
              </form>
            </motion.div>
          )}

          {/* ── History ── */}
          {tab === 'history' && (
            <motion.div key="history" initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }}>
              <div style={{ ...CARD, overflow: 'hidden' }}>
                {transactions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '56px 0' }}>
                    <FileText size={36} style={{ color: '#334155', margin: '0 auto 12px', display: 'block' }}/>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#64748B', margin: '0 0 4px' }}>No transactions yet</p>
                    <p style={{ fontSize: 12, color: '#475569', margin: 0 }}>Make your first deposit to get started</p>
                  </div>
                ) : (
                  <div>
                    {transactions.map((tx, i) => {
                      const isGhsDeposit    = tx.type === 'deposit'    && tx.currency === 'GHS' && tx.rate;
                      const isLocalWithdraw = tx.type === 'withdrawal' && tx.currency && tx.currency !== 'USDT' && tx.amount_local;
                      return (
                        <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                          style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: tx.type === 'deposit'      ? `${G}14` :
                                        tx.type === 'withdrawal'   ? `${R}14` :
                                        tx.type === 'subscription' ? `${P}14` : '#FBBF2414',
                          }}>
                            <TxIcon type={tx.type}/>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 13, fontWeight: 600, color: '#fff', margin: '0 0 2px', textTransform: 'capitalize' }}>{tx.type}</p>
                            <p style={{ fontSize: 11, color: '#64748B', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {tx.description || tx.reference || '—'}
                            </p>
                            {isGhsDeposit && (
                              <p style={{ fontSize: 11, color: '#475569', margin: '2px 0 0' }}>
                                GHS {tx.amount_local?.toFixed(2)} · {tx.network} · 1 USDT = {tx.rate} GHS
                              </p>
                            )}
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                            <p style={{
                              fontSize: 13, fontWeight: 900, margin: 0,
                              color: tx.type === 'deposit' || tx.type === 'referral' ? G :
                                     tx.type === 'convert' ? C : R,
                            }}>
                              {tx.type === 'deposit' || tx.type === 'referral' ? '+' : tx.type === 'convert' ? '⇄' : '−'}
                              {isLocalWithdraw
                                ? `${tx.currency === 'NGN' ? '₦' : ''}${tx.amount_local?.toFixed(2)} ${tx.currency}`
                                : `$${tx.amount_usdt?.toFixed(2)}`}
                            </p>
                            <Badge status={tx.status}/>
                            {isGhsDeposit && tx.reference && (
                              <button type="button" onClick={() => fetchReceipt(tx.reference)}
                                style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: C, fontSize: 11, padding: 0 }}>
                                <FileText size={10}/> Receipt
                              </button>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ── Addresses ── */}
          {tab === 'addresses' && (
            <motion.div key="addresses" initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }}>
              <div style={{ ...CARD, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <p style={{ fontSize: 12, color: '#64748B', margin: 0 }}>Unique TRC20 deposit addresses via merchant API</p>
                  <button type="button" onClick={loadSubWallets}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
                    <RefreshCw size={13}/>
                  </button>
                </div>
                {subWalletsLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '48px 0' }}>
                    <Spin size={20}/><span style={{ fontSize: 13, color: '#64748B' }}>Loading…</span>
                  </div>
                ) : subWallets.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '56px 0' }}>
                    <ExternalLink size={36} style={{ color: '#334155', margin: '0 auto 12px', display: 'block' }}/>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#64748B', margin: '0 0 4px' }}>No addresses yet</p>
                    <p style={{ fontSize: 12, color: '#475569', margin: 0 }}>Use <code style={{ color: G }}>POST /api/v1/usdt/address</code></p>
                  </div>
                ) : (
                  <div>
                    {subWallets.map((sw, i) => (
                      <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                        style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 10, background: `${G}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                          <ArrowDownCircle size={14} style={{ color: G }}/>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                            <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sw.reference}</p>
                            {sw.label && <span style={{ fontSize: 11, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {sw.label}</span>}
                            <span style={{
                              marginLeft: 'auto', flexShrink: 0, fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 700,
                              background: sw.is_active ? `${G}14` : 'rgba(255,255,255,0.04)',
                              color: sw.is_active ? G : '#64748B',
                            }}>
                              {sw.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p style={{ fontSize: 11, fontFamily: 'monospace', color: C, margin: '0 0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sw.address}</p>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 11, color: '#475569' }}>
                              {sw.created_at ? new Date(sw.created_at).toLocaleDateString() : '—'}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <span style={{ fontSize: 12, fontWeight: 900, color: G }}>${sw.balance?.toFixed(2)}</span>
                              <button type="button"
                                onClick={() => { navigator.clipboard.writeText(sw.address); toast.success('Address copied!'); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: 11, padding: 0 }}>
                                <Copy size={10}/> Copy
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      {/* Receipt modal */}
      <AnimatePresence>
        {(receipt || receiptLoading) && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
            onClick={() => setReceipt(null)}>
            <motion.div initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 16 }}
              onClick={e => e.stopPropagation()}
              style={{ ...CARD, width: '100%', maxWidth: 400, overflow: 'hidden', boxShadow: `0 0 60px ${G}08` }}>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#fff', margin: 0 }}>
                  <FileText size={14} style={{ color: P2 }}/> Deposit Receipt
                </h3>
                <button type="button" onClick={() => setReceipt(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
                  <X size={16}/>
                </button>
              </div>

              {receiptLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '48px 0' }}>
                  <Spin size={20}/><span style={{ fontSize: 13, color: '#64748B' }}>Loading receipt…</span>
                </div>
              ) : receipt && (
                <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '10px 0', borderRadius: 10, fontSize: 13, fontWeight: 700,
                    background: receipt.status === 'completed' ? `${G}12` : receipt.status === 'failed' ? `${R}12` : '#FBBF2412',
                    border: `1px solid ${receipt.status === 'completed' ? `${G}30` : receipt.status === 'failed' ? `${R}30` : '#FBBF2430'}`,
                    color: receipt.status === 'completed' ? G : receipt.status === 'failed' ? R : '#FBBF24',
                  }}>
                    {receipt.status === 'completed' && <CheckCircle size={14}/>}
                    {receipt.status.charAt(0).toUpperCase() + receipt.status.slice(1)}
                  </div>
                  <div style={{ ...INNER, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      ['You paid',      `GHS ${receipt.amount_ghs?.toFixed(2)}`],
                      ['You received',  `$${receipt.amount_usdt?.toFixed(4)} USDT`],
                      ['Exchange rate', `1 USDT = GHS ${receipt.rate}`],
                    ].map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#64748B' }}>{label}</span>
                        <span style={{ fontWeight: 700, color: '#fff' }}>{value}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ ...INNER, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      ['Network',   receipt.network],
                      ['Phone',     receipt.phone],
                      ['Name',      receipt.customer_name],
                      ['Reference', receipt.reference],
                      ['Date',      receipt.created_at ? new Date(receipt.created_at).toLocaleString() : '—'],
                    ].map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
                        <span style={{ color: '#64748B', flexShrink: 0 }}>{label}</span>
                        <span style={{ fontWeight: 500, color: '#CBD5E1', textAlign: 'right', wordBreak: 'break-all' }}>{value || '—'}</span>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={() => setReceipt(null)}
                    style={{ width: '100%', padding: '12px 0', borderRadius: 10, cursor: 'pointer', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: '#94A3B8', fontSize: 13, fontWeight: 700 }}>
                    Close
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Layout>
  );
}
